import { isHrTeam, isSuperAdmin } from '@components/Common/Utils/roles';
import type { PortfolioItem } from '@components/JobSeekers/types';

/**
 * The rules behind the Work samples card, kept out of the component so they can be tested alone.
 *
 * The numbers are the backend's (addJobSeekerPortfolioItems refuses past 12) and the bucket's
 * (`trymintly-companygallery`, 10 MB, images and PDF in bucketPolicy.ts). They are checked here too
 * so an HR who picks a 40 MB file or a fifteenth sample is told before anything uploads, not after.
 */

export const MAX_SAMPLES = 12;
export const SAMPLE_MAX_BYTES = 10 * 1024 * 1024;
export const SAMPLE_BUCKET = 'trymintly-companygallery';
const SAMPLE_REGION = 'ap-south-1';

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

/** The file picker's `accept`: extensions and MIME types, because browsers honour one or the other. */
export const SAMPLE_ACCEPT = [
  ...IMAGE_EXTS.map((e) => `.${e}`),
  '.pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
].join(',');

/** Super Admin and the recruiting team add and remove samples. Marketing, and anyone else, views. */
export const canEditWorkSamples = (role?: string | null): boolean => isSuperAdmin(role) || isHrTeam(role);

/** The lower-case extension of a file name or URL, ignoring any query string. */
const extensionOf = (nameOrUrl: string): string => {
  const path = nameOrUrl.split(/[?#]/)[0];
  const dot = path.lastIndexOf('.');
  return dot === -1 ? '' : path.slice(dot + 1).toLowerCase();
};

/** "image", "pdf", or null for anything this card does not take. */
export const fileTypeOf = (nameOrUrl: string): PortfolioItem['fileType'] | null => {
  const ext = extensionOf(nameOrUrl);
  if (ext === 'pdf') return 'pdf';
  return IMAGE_EXTS.includes(ext) ? 'image' : null;
};

/**
 * The backend's portfolio rows as the card reads them.
 *
 * Rows with no usable URL are dropped rather than rendered as broken tiles. `fileType` is the
 * backend's when it sends one and the extension's otherwise; `addedBy` reads null as "candidate",
 * which is what every row written before Mintly could add samples is.
 */
export function normalizePortfolio(raw: unknown): PortfolioItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p) => typeof p?.imageUrl === 'string' && p.imageUrl.trim() !== '')
    .map((p) => {
      const imageUrl: string = p.imageUrl.trim();
      const fileType: PortfolioItem['fileType'] =
        p.fileType === 'pdf' || p.fileType === 'image' ? p.fileType : fileTypeOf(imageUrl) ?? 'image';
      return {
        imageUrl,
        caption: typeof p.caption === 'string' ? p.caption : '',
        uploadedAt: typeof p.uploadedAt === 'string' ? p.uploadedAt : '',
        fileType,
        addedBy: p.addedBy === 'mintly' ? 'mintly' : 'candidate',
        addedById: typeof p.addedById === 'string' && p.addedById ? p.addedById : null,
      };
    });
}

/** `<uuid>-` at the front of a key this console or the site composed. */
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

/**
 * What to call a sample: its caption, else the file's own name out of the URL, with the uploader
 * prefix and the UUID the upload route adds taken off so it reads as the file someone chose.
 */
export function sampleName(item: Pick<PortfolioItem, 'caption' | 'imageUrl'>): string {
  if (item.caption.trim()) return item.caption.trim();
  let last = item.imageUrl.split(/[?#]/)[0];
  try {
    last = decodeURIComponent(last);
  } catch {
    /* a malformed escape: show it as stored */
  }
  const file = last.split('/').pop() ?? '';
  return file.replace(UUID_PREFIX, '') || 'Work sample';
}

/**
 * The URL to record for an uploaded sample. The gallery is public-read, so this is the object's
 * public URL, built from the server's key exactly as the logo uploads build theirs: one encoded
 * segment, because the key carries a `/` and a file name with spaces.
 */
export const sampleObjectUrl = (key: string): string =>
  `https://${SAMPLE_BUCKET}.s3.${SAMPLE_REGION}.amazonaws.com/${encodeURIComponent(key)}`;

export interface PlannedSample {
  file: File;
  fileType: PortfolioItem['fileType'];
}

export interface RejectedSample {
  name: string;
  reason: string;
}

/**
 * Which picked files will upload, and why the rest will not.
 *
 * In the order picked, so when a choice would pass 12 the first ones go and the last are named.
 */
export function planUploads(
  files: File[],
  existingCount: number,
): { accepted: PlannedSample[]; rejected: RejectedSample[] } {
  const accepted: PlannedSample[] = [];
  const rejected: RejectedSample[] = [];
  let room = Math.max(0, MAX_SAMPLES - existingCount);
  for (const file of files) {
    const fileType = fileTypeOf(file.name);
    if (!fileType) {
      rejected.push({ name: file.name, reason: 'not an image or a PDF' });
    } else if (file.size > SAMPLE_MAX_BYTES) {
      rejected.push({ name: file.name, reason: 'over 10 MB' });
    } else if (file.size === 0) {
      rejected.push({ name: file.name, reason: 'the file is empty' });
    } else if (room === 0) {
      rejected.push({ name: file.name, reason: `a candidate can hold at most ${MAX_SAMPLES} work samples` });
    } else {
      accepted.push({ file, fileType });
      room -= 1;
    }
  }
  return { accepted, rejected };
}

/** One line naming every file that did not make it, or null when they all did. */
export function failureMessage(failed: RejectedSample[]): string | null {
  if (failed.length === 0) return null;
  if (failed.length === 1) return `${failed[0].name} was not added: ${failed[0].reason}.`;
  return `${failed.length} files were not added: ${failed.map((f) => `${f.name} (${f.reason})`).join('; ')}.`;
}

/**
 * A reason a person can act on, from whatever an upload or a mutation threw.
 *
 * The BFF's refusals come back as `{ error }` on the response (a non-PDF, a file over the limit);
 * the backend's as "GraphQL errors: <message>", whose message is written for this screen
 * ("A candidate can hold at most 12 work samples").
 */
export function failureReason(err: unknown, fallback = 'the upload failed'): string {
  const server = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (typeof server === 'string' && server.trim()) return server.trim().replace(/\.$/, '');
  const message = String((err as { message?: unknown })?.message ?? '')
    .replace(/^GraphQL errors:\s*/, '')
    .trim();
  // axios's own wording ("Request failed with status code 500", "Network Error") says nothing.
  if (!message || /^(Request failed|Network Error)/i.test(message)) return fallback;
  return message.replace(/\.$/, '');
}

/** "4 of 12", and "· view only" for a role that cannot change them. */
export const countLabel = (count: number, editable: boolean): string =>
  `${count} of ${MAX_SAMPLES}${editable ? '' : ' · view only'}`;

export const humanSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
